import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, MapPin, Briefcase, User, Home, Layers, ChevronDown } from 'lucide-react';
import { QuickCreateEmpresa, QuickCreateArea } from '../QuickCreateModals';
import ImovelCreateModal from '../imoveis/ImovelCreateModal';

/**
 * Dropdown "+Novo" do AppHeader.
 *
 * Items agrupados:
 *  - Negócio: Empresa (quick-create), Área (quick-create), Oferta (link Dashboard)
 *  - Cadastro: Contato (link /contatos), Imóvel (quick-create modal), Lote (em breve)
 *
 * Oferta exige empresa+area em contexto — aqui apenas redireciona para Dashboard,
 * onde o usuário seleciona antes de criar.
 */
export default function QuickCreateDropdown() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'empresa' | 'area' | 'imovel' | null
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleClose = () => setModal(null);
  const handleCreated = () => {
    setModal(null);
    // Força refresh da página para que dados apareçam. Em futuro, usar global cache invalidation.
    window.location.reload();
  };

  const goTo = (path) => {
    setOpen(false);
    navigate(path);
  };

  const Item = ({ icon: Icon, label, onClick, disabled, hint }) => (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs ${
        disabled ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 hover:bg-green-50'
      }`}
    >
      <Icon size={13} className={disabled ? 'text-slate-300' : 'text-green-600'} />
      <span className="flex-1">{label}</span>
      {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
    </button>
  );

  const Group = ({ title, children }) => (
    <div>
      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{title}</div>
      {children}
    </div>
  );

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 text-sm"
          title="Criar novo registro"
        >
          <Plus size={14} /> Novo <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
            <Group title="Negócio">
              <Item icon={Building2} label="Empresa" onClick={() => { setOpen(false); setModal('empresa'); }} />
              <Item icon={MapPin} label="Área" onClick={() => { setOpen(false); setModal('area'); }} />
              <Item icon={Briefcase} label="Oferta" onClick={() => goTo('/')} hint="no Dashboard" />
            </Group>
            <div className="border-t border-slate-100 my-1" />
            <Group title="Cadastro">
              <Item icon={User} label="Contato" onClick={() => goTo('/contatos')} hint="na lista" />
              <Item icon={Home} label="Imóvel" onClick={() => { setOpen(false); setModal('imovel'); }} />
              <Item icon={Layers} label="Lote" disabled hint="em breve" />
            </Group>
          </div>
        )}
      </div>

      {modal === 'empresa' && (
        <QuickCreateEmpresa onClose={handleClose} onCreated={handleCreated} />
      )}
      {modal === 'area' && (
        <QuickCreateArea onClose={handleClose} onCreated={handleCreated} />
      )}
      {modal === 'imovel' && (
        <ImovelCreateModal onClose={handleClose} onCreated={handleCreated} />
      )}
    </>
  );
}
